import { useQuery } from "@apollo/client/react";
import { UsageTrackerEvent, usageTracker, useAnalytics, VariantFlag, Variants } from "@volumental/analytic";
import ScanElseImage from "@volumental/assets/images/scanner/scannerPicker/scan_someone.png";
import ScanSelfImage from "@volumental/assets/images/scanner/scannerPicker/scan_yourself.png";
import { AgeRestrictionModal, Container, Content, Headline, Loader } from "@volumental/common";
import { useState } from "react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { graphql } from "../../api/gql";
import { Header } from "../../components/accessible-components";
import { Modal } from "../../components/modal/modal";
import { UserFlowType } from "../../types/types";
import { GeneralErrorPage } from "../error/GeneralErrorPage";
import { ErrorBox } from "./components/error-box";
import { PickerButton } from "./components/picker-button";
import { GetHelpView } from "./get-help-view";

const GetPickerConfigurationQuery = graphql(`
  query getPickerConfigurationQuery {
    configuration {
      scanner {
        allowSelfScanning
        allowAssistedScanning
      }
    }
  }
`);

interface PickerViewProps {
  onClose: () => void;
  onBack?: () => void;
  onContinue: (scannerType: UserFlowType) => void;
}

export function PickerView({ onClose, onBack, onContinue }: PickerViewProps): JSX.Element {
  const { loading, error, data } = useQuery(GetPickerConfigurationQuery);
  const [showAgeRestriction, setShowAgeRestriction] = useState(false);
  const [showGetHelp, setShowGetHelp] = useState(false);
  const { isVariantActive } = useAnalytics();
  const { t } = useTranslation();

  if (loading) return <Loader />;
  if (error || !data) {
    return <GeneralErrorPage errorText="Missing PickerView configuration" onClose={onClose} />;
  }

  const {
    scanner: { allowAssistedScanning, allowSelfScanning },
  } = data.configuration;

  const showGetHelpPrompt = (): void => {
    usageTracker.track(UsageTrackerEvent.GetHelpPromptShown);
    setShowGetHelp(true);
  };

  const handleSoloContinue = (): void => {
    if (isVariantActive(VariantFlag.promoteAssisted, Variants.treatment)) {
      showGetHelpPrompt();
      return;
    }
    onContinue(UserFlowType.Solo);
  };

  const handleGetHelpSolo = (): void => {
    usageTracker.track(UsageTrackerEvent.GetHelpPromptSelect, { choice: UserFlowType.Solo });
    onContinue(UserFlowType.Solo);
  };

  const handleGetHelpAssisted = (): void => {
    usageTracker.track(UsageTrackerEvent.GetHelpPromptSelect, { choice: UserFlowType.AssistedAdult });
    onContinue(UserFlowType.AssistedAdult);
  };

  const handleAssistedContinue = (): void => {
    usageTracker.track(UsageTrackerEvent.AgeRestriction, {
      "user choise": "confirm",
    });
    setShowAgeRestriction(false);
    onContinue(UserFlowType.Assisted);
  };

  if (showGetHelp) {
    return (
      <GetHelpView
        onBack={() => {
          setShowGetHelp(false);
        }}
        onClose={onClose}
        onSelfScan={handleGetHelpSolo}
        onAssistedScan={handleGetHelpAssisted}
      />
    );
  }

  return (
    <Container>
      <Header back={onBack} close={onClose} />
      <Content disableScrollbar>
        <Headline className="text-xl my-4 font-normal">{t("multiIntro.titleMoveInstruction")}</Headline>

        {allowSelfScanning ? (
          <PickerButton
            onClick={handleSoloContinue}
            imageSrc={ScanSelfImage}
            title={t("multiIntro.selfScanningMoveInstruction")}
            altText={t("imageAlt.pickerSolo")}
          />
        ) : undefined}
        {allowAssistedScanning ? (
          <PickerButton
            onClick={() => {
              setShowAgeRestriction(true);
            }}
            imageSrc={ScanElseImage}
            title={t("multiIntro.assistedScanningMoveInstruction")}
            altText={t("imageAlt.pickerAssisted")}
          />
        ) : undefined}
        {!allowSelfScanning && !allowAssistedScanning && <ErrorBox />}
      </Content>
      <Modal
        modalTitle={t("modalTitles.ageRestriction")}
        show={showAgeRestriction}
        close={() => {
          setShowAgeRestriction(false);
        }}
        fitContent
      >
        <AgeRestrictionModal
          proceed={handleAssistedContinue}
          title={t("ageRestriction.title")}
          description={t("ageRestriction.description")}
          acceptText={t("button.confirm")}
        />
      </Modal>
    </Container>
  );
}
